import { Component, EventEmitter, Input, Output, OnDestroy, inject } from '@angular/core';
import { CommonModule, ViewportScroller } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { AuthService } from '@services/auth.service';
import { QuoteService } from '@services/quote.service';
import { QuoteFlowService } from '@services/quote-flow.service';
import { NotificationsService, Notification as AppNotification } from '@services/notifications.service';
import { Subscription } from 'rxjs';
import { MenuItem } from './type';
import { BuildingModal } from '../../../features/landing/components/building-modal/building-modal';
import { WorkTypeSelectionModalComponent, ConstructionType, WorkTypeModalConfig } from '../../../features/page/quote/modal/work-type-selection-modal.component';
import { NAVIGATION_ROUTES } from '@shared/constants';
import { SideMenuButtonComponent, SideMenuButtonData } from '../../design-system/molecules/side-menu-button/side-menu-button.component';
import { openTrainingUrl } from '@utils/open-training-url';
import { SvgIconComponent } from '@components/svg-icon/svg-icon.component';
import { SvgIcon } from '@services/svg-icon.service';
@Component({
  selector: 'app-side-menu',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, BuildingModal, SideMenuButtonComponent, SvgIconComponent],
  templateUrl: './side-menu.component.html'
})
export class SideMenuComponent implements OnDestroy {
  protected readonly SvgIcon = SvgIcon;

  @Input() isOpen = false;
  @Input() searchValue = '';
  @Output() close = new EventEmitter<void>();

  sentQuotesCount: number = 0;
  showBuildingModal: boolean = false;
  showTrainingConstructionModal: boolean = false;
  private unreadCount = 0;
  private notifSub?: Subscription;

  // Notificaciones: lista y suscripción
  notificationsList: AppNotification[] = [];
  private notificationsListSub?: Subscription;
  public showNotificationsPanel = false;

   private quoteFlowService = inject(QuoteFlowService);

  // Quick action buttons data
  readonly quickActionButtons: SideMenuButtonData[] = [
    {
      iconSrc: 'assets/icons/calcular-materiales.png',
      title: 'Calcular Materiales',
      showPill: true,
      pillText: 'Nuevo'
    },
    {
      iconSrc: 'assets/icons/cotizar-materiales.png',
      title: '¡Arma tu Lista de Materiales!'
    },
    {
      iconSrc: 'assets/icons/productos-unicon.png',
      title: 'Pedir productos UNICON'
    },
    {
      iconSrc: 'assets/icons/crear-contrato.png',
      title: 'Crear contrato'
    }
  ];

  menu: MenuItem[] = [
    { icon: 'assets/icons/recent.svg', label: 'Recientes', route: [NAVIGATION_ROUTES.INICIO] },
    // { icon: 'assets/icons/account.svg', label: 'Mi cuenta', route: [NAVIGATION_ROUTES.PROFILE] },
    { icon: 'assets/icons/proyect.svg', label: 'Mis proyectos', route: [NAVIGATION_ROUTES.QUOTES_LIST] },
    // { icon: 'assets/icons/docs.svg', label: 'Documentos legales', route: ['/legal'] },
  ];

  constructor(public auth: AuthService,
    private quoteService: QuoteService,
    private quoteFlow: QuoteFlowService,
    private notificationsService: NotificationsService,
    private router: Router,
    private viewportScroller: ViewportScroller,
    private bottomSheet: MatBottomSheet) {
    this.loadSentQuotesCount();

    // Suscribir al contador de notificaciones no leídas y actualizar badge
    this.notifSub = this.notificationsService.unreadCount.subscribe((n) => {
      this.unreadCount = n ?? 0;
      this.updateProjectBadge();
    });

    // Suscribirse a la lista de notificaciones para mostrar en el panel
    this.notificationsListSub = this.notificationsService.notifications.subscribe((list) => {
      this.notificationsList = list || [];
    });
  }

  ngOnDestroy(): void {
    if (this.notifSub) this.notifSub.unsubscribe();
    if (this.notificationsListSub) this.notificationsListSub.unsubscribe();
  }

  get userName(): string {
    const user = this.auth.user();
    if (!user) return '';

    // Intentar obtener el nombre completo desde diferentes campos
    const anyUser = user as any;

    // Prioridad 1: firstname + lastname (lowercase, como viene del servicio)
    const firstname = anyUser.firstname || anyUser.first_name || anyUser.firstName || '';
    const lastname = anyUser.lastname || anyUser.last_name || anyUser.lastName || '';
    if (firstname || lastname) {
      return `${firstname} ${lastname}`.trim();
    }

    // Prioridad 2: campo 'name' completo
    if (anyUser.name) {
      return anyUser.name;
    }

    return '';
  }

  onOverlayClick() { this.close.emit(); }

  goToHome() {
    this.close.emit();
    this.router.navigate([NAVIGATION_ROUTES.INICIO]);
  }

  onCloseClick(section: string) {
    this.close.emit();
    if(section === 'multimedia') {
      // Navegar a inicio primero si no estamos ahí, luego hacer scroll
      if (this.router.url !== NAVIGATION_ROUTES.INICIO) {
        this.router.navigate([NAVIGATION_ROUTES.INICIO]).then(() => {
          setTimeout(() => {
            this.scrollToLearnSection();
          }, 500); // Delay adicional para permitir que la página cargue
        });
      } else {
        setTimeout(() => {
          this.scrollToLearnSection();
        }, 300); // Delay para permitir que la animación de cierre del menú termine
      }
    }
    if(section === 'training') {
      this.openTrainingUrl();
    }
    if(section === 'quote-step-1') {
      // Abrir bottom sheet de selección de tipo de construcción
      this.openWorkTypeSelector();
    }
  }

  logout() {
    this.quoteFlowService.reset();
    this.auth.logout();
  }

  goToUniconChat() {
    const currentUser = this.auth.user();
    this.router.navigate([NAVIGATION_ROUTES.UNICON_CHAT], {
      queryParams: {
        firstName: currentUser?.firstname || currentUser?.name?.split(' ')[0] || 'Usuario',
        lastName: currentUser?.lastname || currentUser?.name?.split(' ').slice(1).join(' ') || 'Maestro',
        userIdOnBusiness: currentUser?.id || 'guest'
      }
    });
    this.close.emit(); // Cerrar el menú después de navegar
  }

  loadSentQuotesCount() {
    this.quoteService.getSentQuotesCount().subscribe({
      next: (res) => {
        this.sentQuotesCount = res.sentCount;
        this.updateProjectBadge();
      },
      error: () => {
        this.sentQuotesCount = 0;
        this.updateProjectBadge();
      }
    });
  }

  /**
   * Actualiza el badge de la entrada 'Mis proyectos' mostrando el contador de notificaciones
   * si existe (>0). Si no hay notificaciones, muestra el contador de cotizaciones enviadas.
   */
  private updateProjectBadge() {
    const quoteMenu = this.menu.find(m => Array.isArray(m.route) && m.route[0] === NAVIGATION_ROUTES.QUOTES_LIST);
    if (!quoteMenu) return;

    if (this.unreadCount > 0) {
      quoteMenu.badge = this.unreadCount;
    } else {
      // Mostrar sentQuotesCount sólo si no hay notificaciones no leídas
      quoteMenu.badge = this.sentQuotesCount > 0 ? this.sentQuotesCount : undefined;
    }
  }

  /** Abrir/cerrar panel de notificaciones dentro del side-menu */
  toggleNotificationsPanel(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.showNotificationsPanel = !this.showNotificationsPanel;
    if (this.showNotificationsPanel) {
      // cargar primera página al abrir (no bloquear la UI) - ambos endpoints
      Promise.all([
        this.notificationsService.fetchPage(1, 50).catch(() => []),
        this.notificationsService.fetchContractsPage(1, 50).catch(() => [])
      ]).then(() => {
        // Las notificaciones ya están combinadas en el BehaviorSubject
      });
    }
  }

  /** Marca una notificación como leída */
  async markAsRead(n: AppNotification) {
    const id = n.id || (n as any).notificationId || '';
    if (!id) return;
    try {
      await this.notificationsService.markAsRead(id);
      // opcional: actualizar lista pidiendo la página 1
      this.notificationsService.fetchPage(1, 50).catch(() => { });
    } catch (e) {
    }
  }

 closeConstructionModal(): void {
    this.showBuildingModal = false;
  }

  closeTrainingConstructionModal(): void {
    this.showTrainingConstructionModal = false;
  }

  private openWorkTypeSelector(): void {
    this.bottomSheet.open(WorkTypeSelectionModalComponent, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'custom-bottom-sheet-quote',
    });
  }

  private openCalculatorTypeSelector(): void {
    const config: WorkTypeModalConfig = {
      title: '¿Qué vas a calcular?',
      subtitle: '',
      targetRoute: NAVIGATION_ROUTES.CALCULATOR,
      mode: 'calculator'
    };

    this.bottomSheet.open(WorkTypeSelectionModalComponent, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'custom-bottom-sheet-quote',
      data: { config }
    });
  }

  onQuickActionClick(index: number): void {
    this.close.emit();
    
    switch (index) {
      case 0: // Calcular Materiales
        this.openCalculatorTypeSelector();
        break;
      case 1: // Cotizar materiales
        this.openWorkTypeSelector();
        break;
      case 2: // Pedir productos UNICON
        this.goToUniconChat();
        break;
      case 3: // Crear contrato
        this.router.navigate([NAVIGATION_ROUTES.CONTRACT_NEW]);
        break;
      case 4: // Capacitarme
        this.openTrainingUrl();
        break;
    }
  }

  private openTrainingUrl(): void {
    openTrainingUrl(this.auth.constructorRedirectUrl());
  }

  private scrollToLearnSection(): void {
    // Buscar el elemento de la sección de aprender con múltiples selectores
    const learnElement = document.querySelector('#aprende') ||
                        document.querySelector('app-learn-section') ||
                        document.querySelector('[id="aprende"]') ||
                        document.querySelector('.learn-section');

    if (learnElement) {
      learnElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    } else {
      // Fallback usando ViewportScroller si no encuentra el elemento
      this.viewportScroller.scrollToAnchor('aprende');

      // Segundo fallback: scroll a posición estimada si tiene el componente
      setTimeout(() => {
        const learnComponent = document.querySelector('app-learn-section');
        if (learnComponent) {
          learnComponent.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        } else {
          console.log('No se encontró ningún elemento de la sección de aprender');
        }
      }, 100);
    }
  }
}
